import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { isEqual } from 'lodash'
import { useRef } from 'react'
import { usePortalQueries } from 'src/Libs/api'
import { WallboxConfigurationModel } from 'src/Libs/wallboxes'
import { getWallboxConfiguration } from '../api'
import useConnectedWallboxesQuery from './use-connected-wallboxes-query'
import { getWallboxConfigurationQueryKey } from './use-wallbox-configuration'

type UseWallboxConfigurationQueries = {
  data: WallboxConfigurationModel[]
  isLoading: boolean
  isError: boolean
}

const useWallboxConfigurationQueries = (enabled = true): UseWallboxConfigurationQueries => {
  const authFetch = useAuthFetchWithResponse()
  const { data: connectedWallboxes, isLoading: isConnectedWallboxesLoading, isError: isConnectedWallboxesError } = useConnectedWallboxesQuery(enabled)
  const configurationQueries = usePortalQueries(
    connectedWallboxes?.map(({ storageSerialNo: wallboxStorageId, wallboxIndex }) => ({
      queryKey: getWallboxConfigurationQueryKey(wallboxStorageId, wallboxIndex.toString()),
      queryFn: () => getWallboxConfiguration(wallboxStorageId, wallboxIndex, authFetch),
      enabled: Boolean(wallboxStorageId) && enabled,
    })) || []
  )

  const configurations = configurationQueries
    .map(({ data }) => data)
    .filter((data): data is WallboxConfigurationModel => data !== null && data !== undefined)

  // usePortalQueries has no `combine`, so map/filter yields a new array on every render. Keep the reference stable
  // while the content is unchanged, otherwise consumers (useMemo/useEffect deps) re-run in a loop.
  const stableConfigurationsRef = useRef(configurations)
  if (!isEqual(stableConfigurationsRef.current, configurations)) stableConfigurationsRef.current = configurations

  return {
    data: stableConfigurationsRef.current,
    isLoading: Boolean(isConnectedWallboxesLoading) || configurationQueries.some(({ isLoading }) => isLoading),
    isError: Boolean(isConnectedWallboxesError) || configurationQueries.some(({ isError }) => isError),
  }
}

export default useWallboxConfigurationQueries
